import React, { useState, useEffect } from "react";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Award, Users, Target, Clock, Quote, Phone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const About = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  const stats = [
    { icon: Award, label: "Projetos Premiados", value: "40+" },
    { icon: Users, label: "Equipe Especializada", value: "20+" },
    { icon: Target, label: "Satisfação dos Clientes", value: "98%" },
    { icon: Clock, label: "Tempo Médio Entrega", value: "20 dias" },
  ];

  // Buscar depoimentos da API
  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API}/depoimentos`);

        if (response.ok) {
          const data = await response.json();
          setTestimonials(data);
        } else {
          const { testimonials: mockTestimonials } = await import(
            "../data/mockData"
          );
          setTestimonials(mockTestimonials);
        }
      } catch (error) {
        console.error("Erro ao carregar depoimentos:", error);
        const { testimonials: mockTestimonials } = await import(
          "../data/mockData"
        );
        setTestimonials(mockTestimonials);
      } finally {
        setLoading(false);
      }
    };

    fetchTestimonials();
  }, []);

  return (
    <section
      id="sobre"
      className="py-20 bg-gradient-to-br from-gray-50 to-white overflow-x-clip"
    >
      <div className="mx-auto w-full max-w-7xl px-4 min-w-0">
        <div className="grid lg:grid-cols-2 gap-16 items-center min-w-0">
          {/* Left Content */}
          <div className="min-w-0 w-full text-center lg:text-left">
            <Badge className="mb-6 bg-red-50 text-[#FF1616] hover:bg-red-100 px-4 py-2 font-bold tracking-wide">
              SOBRE A FORTTANA EVENTOS
            </Badge>

            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6 leading-tight tracking-tight">
              10+ ANOS{" "}
              <span className="text-[#FF1616] block">CRIANDO</span>{" "}
              EXPERIÊNCIAS
            </h2>

            <div className="space-y-4 text-lg text-gray-600 mb-8 min-w-0">
              <p className="leading-relaxed break-words">
                <strong>Mais de 10 anos</strong> transformando ideias em
                experiências memoráveis. Somos especialistas em{" "}
                <strong>stands, quiosques, ambientações e cenografias</strong>{" "}
                que conectam marcas ao seu público-alvo.
              </p>

              <p className="leading-relaxed break-words">
                Nossa paixão é criar projetos únicos que não apenas impressionam
                visualmente, mas também <strong>geram resultados concretos</strong>{" "}
                para nossos clientes. Combinamos criatividade, tecnologia e
                execução impecável.
              </p>
            </div>

            {/* skills: central no mobile */}
            <div className="flex flex-wrap gap-4 mb-8 justify-center lg:justify-start">
              {["PROJETO 3D", "CONSTRUÇÃO", "MONTAGEM", "LOGÍSTICA"].map(
                (skill) => (
                  <Badge
                    key={skill}
                    variant="outline"
                    className="px-3 py-1 border-red-200 text-[#FF1616] font-bold tracking-wide"
                  >
                    {skill}
                  </Badge>
                )
              )}
            </div>

            {/* botões: central no mobile + quebra bonita */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Button
                size="lg"
                className="bg-[#FF1616] hover:bg-red-700 text-white font-bold tracking-wide"
                type="button"
              >
                NOSSA EQUIPE
              </Button>

              <Button
                size="lg"
                variant="outline"
                className="border-[#FF1616] text-[#FF1616] hover:bg-red-50 font-bold tracking-wide"
                onClick={() =>
                  window.open(
                    "https://wa.me/5561981255035",
                    "_blank",
                    "noopener,noreferrer"
                  )
                }
                type="button"
              >
                <Phone className="mr-2 w-4 h-4" />
                (61) 98125-5035
              </Button>
            </div>
          </div>

          {/* Right Content - Stats & Image */}
          <div className="relative min-w-0 w-full mx-auto lg:mx-0">
            <div className="grid grid-cols-2 gap-6 mb-8 min-w-0">
              {stats.map((stat, index) => {
                const IconComponent = stat.icon;
                return (
                  <Card
                    key={index}
                    className="min-w-0 text-center hover:shadow-lg transition-all duration-300 border-0 bg-white"
                  >
                    <CardContent className="p-6 min-w-0">
                      <div className="w-12 h-12 bg-gradient-to-r from-red-50 to-red-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                        <IconComponent className="w-6 h-6 text-[#FF1616]" />
                      </div>
                      <div className="text-2xl font-black text-gray-900 mb-1">
                        {stat.value}
                      </div>
                      <div className="text-sm text-gray-600 font-medium tracking-wide break-words">
                        {stat.label}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <div className="relative rounded-2xl overflow-hidden mx-auto lg:mx-0">
              <img
                src="https://images.unsplash.com/photo-1692166525289-be57203f44a9"
                alt="Equipe Forttana construindo stand"
                className="w-full h-64 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              <div className="absolute bottom-6 left-6 text-white">
                <div className="text-lg font-bold tracking-wide">
                  NOSSA EQUIPE EM AÇÃO
                </div>
                <div className="text-sm opacity-90">
                  Profissionais especializados e comprometidos
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Testimonials Section */}
        <div className="mt-20 min-w-0">
          <div className="text-center mb-12 min-w-0">
            <h3 className="text-3xl md:text-4xl font-black text-gray-900 mb-4 tracking-wide">
              O QUE NOSSOS CLIENTES DIZEM
            </h3>
            <p className="text-lg text-gray-600 font-medium">
              Depoimentos reais de quem confia no nosso trabalho
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 min-w-0">
            {testimonials.map((testimonial, index) => (
              <Card
                key={index}
                className="min-w-0 relative hover:shadow-xl transition-all duration-300 border-0 bg-white"
              >
                <CardContent className="p-8 min-w-0">
                  <div className="absolute -top-4 left-8 w-8 h-8 bg-[#FF1616] rounded-full flex items-center justify-center">
                    <Quote className="w-4 h-4 text-white" />
                  </div>

                  <p className="text-gray-600 mb-6 leading-relaxed italic break-words">
                    "{testimonial.text}"
                  </p>

                  <div className="flex items-center min-w-0">
                    <div className="w-12 h-12 bg-[#FF1616] rounded-full flex items-center justify-center text-white font-black mr-4 shrink-0">
                      {testimonial.avatar}
                    </div>
                    <div className="min-w-0">
                      <div className="font-bold text-gray-900 tracking-wide break-words">
                        {testimonial.name}
                      </div>
                      <div className="text-sm text-gray-600 break-words">
                        {testimonial.company}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* opcional: se quiser tratar loading de forma elegante */}
          {loading && testimonials.length === 0 && (
            <div className="mt-8 text-center text-sm text-gray-500 font-medium">
              Carregando depoimentos...
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default About;
